import React, { createContext, useContext, useState } from "react";
import { SupplierPayment } from "../types/supplierPayment";
import {
    getSupplierPayments,
    createSupplierPayment,
    updateSupplierPayment,
    deleteSupplierPayment
} from "../api/supplierPaymentApi";
import { handleWithAlert } from "../utils/handleWithAlert";

interface SupplierPaymentContextProps {
    supplierPayments: SupplierPayment[];
    addSupplierPayment: (data: Omit<SupplierPayment, "id">) => Promise<SupplierPayment>;
    updateSupplierPayment: (data: SupplierPayment) => Promise<void>;
    deleteSupplierPayment: (id: string) => Promise<void>;
    fetchSupplierPayments: () => Promise<void>;
}

const SupplierPaymentContext = createContext<SupplierPaymentContextProps | undefined>(undefined);

export const SupplierPaymentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [supplierPayments, setSupplierPayments] = useState<SupplierPayment[]>([]);

    const fetchSupplierPayments = async () => {
        const list = await handleWithAlert(getSupplierPayments, { errorMessage: "Failed to fetch payments" });
        if (list) setSupplierPayments(list.map((r: any) => ({ ...r, id: r._id || r.id })));
    };

    const addSupplierPayment = async (data: Omit<SupplierPayment, "id">) => {
        const created = await handleWithAlert(
            () => createSupplierPayment(data),
            { successMessage: "Payment created!", errorMessage: "Failed to create", showSuccess: true }
        );
        if (created) fetchSupplierPayments();
        return created as SupplierPayment;
    };

    const updateSupplierPaymentFn = async (updated: SupplierPayment) => {
        const res = await handleWithAlert(
            () => updateSupplierPayment(updated.id!, updated),
            { successMessage: "Payment updated!", errorMessage: "Failed to update", showSuccess: true }
        );
        if (res) fetchSupplierPayments();
    };

    const deleteSupplierPaymentFn = async (id: string) => {
        const res = await handleWithAlert(
            () => deleteSupplierPayment(id),
            { successMessage: "Payment deleted!", errorMessage: "Failed to delete", showSuccess: true }
        );
        if (res !== null) setSupplierPayments(prev => prev.filter(r => r.id !== id));
    };

    return (
        <SupplierPaymentContext.Provider value={{
            supplierPayments,
            addSupplierPayment,
            updateSupplierPayment: updateSupplierPaymentFn,
            deleteSupplierPayment: deleteSupplierPaymentFn,
            fetchSupplierPayments
        }}>
            {children}
        </SupplierPaymentContext.Provider>
    );
};

export const useSupplierPayment = () => {
    const ctx = useContext(SupplierPaymentContext);
    if (!ctx) throw new Error("useSupplierPayment must be inside SupplierPaymentProvider");
    return ctx;
};
